import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import React from 'react';
import { useThemeColors } from '../../providers/ThemeProvider';
import { cn } from '../../utils/cn';
import { Icon } from '../base/Icon';

interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
	error?: boolean;
	fullWidth?: boolean;
	icon?: React.ReactNode;
	iconPosition?: 'left' | 'right';
	size?: 'xs' | 'sm' | 'md' | 'lg';
}

export const Input = React.forwardRef<HTMLInputElement, InputProps>(
	(
		{
			error = false,
			fullWidth = true,
			icon,
			iconPosition = 'left',
			className,
			disabled,
			type = 'text',
			size = 'md',
			style,
			...props
		},
		ref,
	) => {
		const colors = useThemeColors();

		// Size-based styling
		const sizeClasses = {
			xs: 'px-1.5 py-2 text-xs',
			sm: 'px-2.5 py-1.5 text-sm',
			md: 'px-3 py-2 text-sm',
			lg: 'px-4 py-2.5 text-base',
		}[size];

		// Adjust icon padding based on size
		const iconPaddingClasses = {
			xs: { left: 'pl-8', right: 'pr-8' },
			sm: { left: 'pl-9', right: 'pr-9' },
			md: { left: 'pl-10', right: 'pr-10' },
			lg: { left: 'pl-11', right: 'pr-11' },
		}[size];

		const input = (
			<input
				ref={ref}
				type={type}
				disabled={disabled}
				className={cn(
					// Base styles
					'block',
					fullWidth ? 'w-full' : 'w-auto',

					// Size-based styling
					sizeClasses,

					// Use form theme classes for consistent styling
					colors.classes.formFieldBase,

					// State-based styling
					error
						? colors.classes.formFieldError
						: colors.classes.formFieldDefault,

					// Focus state
					colors.classes.formFieldFocus,

					disabled && colors.classes.formFieldDisabled,

					// Icon classes for CSS targeting
					icon && iconPosition === 'left' && 'has-icon-left',
					icon && iconPosition === 'right' && 'has-icon-right',

					// Icon padding - override base CSS padding on the icon side
					icon && iconPosition === 'left' && iconPaddingClasses.left,
					icon && iconPosition === 'right' && iconPaddingClasses.right,

					// PD-13574: hide native number-input spinners everywhere. Firefox
					// renders in-field arrows that crowd out 2+ digit values; our
					// numeric fields either have their own +/- steppers or don't want
					// spinners at all.
					type === 'number' &&
						'[&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none',

					// User's custom classes
					className,
				)}
				style={
					{
						// Firefox half of the spinner hiding (no pseudo-element for it)
						...(type === 'number' ? { MozAppearance: 'textfield' } : {}),
						...style,
					} as React.CSSProperties
				}
				{...props}
			/>
		);

		if (!icon) {
			return input;
		}

		return (
			<div className="relative">
				{icon && (
					<div
						className={cn(
							'absolute inset-y-0 flex items-center pointer-events-none',
							iconPosition === 'left' ? 'left-0 pl-3' : 'right-0 pr-3',
						)}
					>
						<span
							className={cn(
								colors.classes.textSecondary,
								error && colors.classes.textDanger,
							)}
						>
							{typeof icon === 'object' &&
							icon != null &&
							('iconName' in icon || 'prefix' in icon) ? (
								<Icon icon={icon as IconDefinition} />
							) : (
								icon
							)}
						</span>
					</div>
				)}
				{input}
			</div>
		);
	},
);

Input.displayName = 'Input';
